import {useLocalStorage} from 'platform/components';
import {match} from 'ts-pattern';

import {useEffect, useState, useCallback, useMemo, ReactNode} from 'react';

import {getSafely} from 'shared/web';

import {useSourcingPage} from '../../hooks/useSourcingPage';
import {AllOffersPage} from '../../pages/AllOffers/AllOffersPage';
import {
  UpdateFiltersFunction,
  FiltersContextProvider,
} from '../../pages/AllOffers/components/FiltersContext/FiltersContext';
import {DEFAULT_FILTERS, FiltersType} from '../../pages/AllOffers/types/FiltersType';
import {hasSomeFilters} from '../../pages/AllOffers/utils/hasSomeFilters';
import {migrateFiltersFromOldStructure} from '../../pages/AllOffers/utils/migrateFiltersFromOldStructure';
import {UserCarvagoBuyPage} from '../../pages/UserCarvagoBuy/UserCarvagoBuyPage';
import {UserComparisonPage} from '../../pages/UserComparison/UserComparisonPage';
import {UserFavouritePage} from '../../pages/UserFavourite/UserFavouritePage';
import {UserHiddenPage} from '../../pages/UserHidden/UserHiddenPage';
import {UserToBuyPage} from '../../pages/UserToBuy/UserToBuyPage';
import {SourcingPage} from '../../types/SourcingPage';

export const SourcingListingPage = () => {
  const sourcingPage = useSourcingPage();

  const [initialFilters, saveFiltersToLocalStorage] = useLocalStorage<FiltersType>(
    'sourcingFilters',
    DEFAULT_FILTERS
  );
  const migratedFilters = migrateFiltersFromOldStructure(initialFilters);
  const [filters, setFilters] = useState<FiltersType>(migratedFilters);

  useEffect(() => {
    saveFiltersToLocalStorage(filters);
  }, [saveFiltersToLocalStorage, filters]);

  const onUpdateFilters = useCallback<UpdateFiltersFunction>(
    (key, value) =>
      setFilters((filters) => {
        const currentValue = getSafely(filters, key);
        return {
          ...filters,
          [key]: typeof value === 'function' ? value(currentValue) : value,
        };
      }),
    []
  );

  const onClearFilters = useCallback(() => setFilters(DEFAULT_FILTERS), []);

  const contextValue = useMemo(
    () => ({
      filters,
      // Derived boolean pre-computed here so consumers that only need
      // hasFilters don't subscribe to the full filters object (rerender-derived-state).
      hasFilters: hasSomeFilters(filters),
      onChange: setFilters,
      onUpdateFilters,
      onClearFilters,
    }),
    [filters, onUpdateFilters, onClearFilters]
  );

  const currentPage = match<SourcingPage, ReactNode>(sourcingPage)
    .with(SourcingPage.COMPARISON_PAGE, () => <UserComparisonPage />)
    .with(SourcingPage.CART_PAGE, () => <UserToBuyPage />)
    .with(SourcingPage.HIDDEN_PAGE, () => <UserHiddenPage />)
    .with(SourcingPage.FAVORITES_PAGE, () => <UserFavouritePage />)
    .with(SourcingPage.CARVAGO_BUY_PAGE, () => <UserCarvagoBuyPage />)
    .with(SourcingPage.DEFAULT, () => <AllOffersPage />)
    .exhaustive();

  return <FiltersContextProvider value={contextValue}>{currentPage}</FiltersContextProvider>;
};
